import React from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Autocomplete from '@mui/material/Autocomplete'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import { useGetAllActiveMerchantsIdAndNameCachedQuery, useGetAllCourierPartnersCachedQuery } from '../../redux/commonApiCalls'
import { STATUS_GROUPS, THRESHOLDS } from './stuckUtils'

const COURIER_FILTER = {
  andfilter: [{ field: 'is_deleted', operator: 'eq', value: '0' }],
  offset: 0, limit: 1000, skipCount: true,
  includes: 'id,name,prozoId,parent,isExternal',
}

/**
 * filters = { segment: 'D2C'|'PTL', thresholdHours, merchantOids: [], courierOids: [], statusGroups: [], breachedOnly }
 * The mail = D2C (or PTL), 48 hours, everything else unfiltered.
 */
export default function StuckFilters({ filters, onChange, disabled }) {
  const { data: merchantsRaw = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()
  const merchants = merchantsRaw
    .filter(m => m.is_deleted === '0')
    .map(m => ({ label: m.companyName ? `${m.name} (${m.companyName})` : m.name, value: m.id }))

  const { data: couriersRaw = [] } = useGetAllCourierPartnersCachedQuery(COURIER_FILTER)
  const couriers = (couriersRaw || [])
    .filter(c => !c.isExternal)
    .map(c => ({ label: c.prozoId ? `${c.name} (${c.prozoId})` : c.name, value: c.id }))
    .sort((a, b) => a.label.localeCompare(b.label))

  const set = (patch) => onChange({ ...filters, ...patch })

  return (
    <Paper variant='outlined' sx={{ p: 1.5, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
      <ToggleButtonGroup size='small' exclusive value={filters.segment} onChange={(_, v) => v && set({ segment: v })} disabled={disabled}>
        <ToggleButton value='D2C' sx={{ px: 2 }}>D2C</ToggleButton>
        <ToggleButton value='PTL' sx={{ px: 2 }}>PTL</ToggleButton>
      </ToggleButtonGroup>

      <TextField
        select size='small' label='No update for' value={filters.thresholdHours}
        onChange={(e) => set({ thresholdHours: Number(e.target.value) })}
        sx={{ width: 150 }} disabled={disabled}
      >
        {THRESHOLDS.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
      </TextField>

      <ToggleButtonGroup
        size='small' value={filters.statusGroups}
        onChange={(_, v) => set({ statusGroups: v })}
        disabled={disabled}
        sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5 } }}
      >
        {STATUS_GROUPS.map(g => <ToggleButton key={g.key} value={g.key}>{g.label}</ToggleButton>)}
      </ToggleButtonGroup>

      <Autocomplete
        multiple size='small' limitTags={1} disableCloseOnSelect
        options={merchants}
        value={merchants.filter(m => (filters.merchantOids || []).includes(m.value))}
        isOptionEqualToValue={(o, v) => o.value === v.value}
        onChange={(_, sel) => set({ merchantOids: sel.map(m => m.value) })}
        renderInput={(params) => <TextField {...params} placeholder='All merchants' />}
        sx={{ minWidth: 240 }} disabled={disabled}
      />

      <Autocomplete
        multiple size='small' limitTags={1} disableCloseOnSelect
        options={couriers}
        value={couriers.filter(c => (filters.courierOids || []).includes(c.value))}
        isOptionEqualToValue={(o, v) => o.value === v.value}
        onChange={(_, sel) => set({ courierOids: sel.map(c => c.value) })}
        renderInput={(params) => <TextField {...params} placeholder='All couriers' />}
        sx={{ minWidth: 240 }} disabled={disabled}
      />

      <FormControlLabel
        sx={{ ml: 0.5 }}
        control={<Switch size='small' checked={Boolean(filters.breachedOnly)} onChange={(e) => set({ breachedOnly: e.target.checked })} disabled={disabled} />}
        label={<Box component='span' sx={{ fontSize: 13, fontWeight: 600 }}>Past expected date only</Box>}
      />
    </Paper>
  )
}
